import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { fetchAdminUserById, updateAdminUser } from '@/api/adminUsers';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

function Main() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setLoadError('');

    void fetchAdminUserById(id)
      .then((user) => {
        if (cancelled) return;
        if (user.isSuperAdmin) {
          setLoadError('Super admin cannot be edited');
          return;
        }
        setUsername(user.username);
        setName(user.name);
        setEmail(user.email);
        setIsSuperAdmin(user.isSuperAdmin);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load admin user');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || isSuperAdmin) return;

    setFormError('');
    setFormSuccess('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setFormError('Name is required');
      return;
    }
    if (!trimmedEmail) {
      setFormError('Email is required');
      return;
    }
    if (password && password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (password && password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await updateAdminUser(id, {
        name: trimmedName,
        email: trimmedEmail,
        ...(password ? { password } : {}),
      });
      setFormSuccess('Admin user updated successfully');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update admin user');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh] text-slate-400">
        <Lucide icon="Loader" className="w-6 h-6 animate-spin mr-2" />
        Loading user…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <div className="box rounded-xl border border-danger/20 px-6 py-5 text-danger">{loadError}</div>
        <Button
          type="button"
          variant="outline-primary"
          className="mt-4"
          onClick={() => navigate('/manage-users')}
        >
          Back to Manage Users
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <Link
          to="/manage-users"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-2"
        >
          <Lucide icon="ArrowLeft" className="w-4 h-4 mr-1.5" />
          Back to Manage Users
        </Link>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Edit Admin User</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Update admin account details</p>
      </div>

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm max-w-xl">
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Username:{' '}
          <span className="font-medium text-slate-700 dark:text-slate-200">{username}</span>
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              New password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-slate-500">Leave blank to keep the current password.</p>
          </div>

          {password && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Confirm new password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
                autoComplete="new-password"
              />
            </div>
          )}

          {formError && (
            <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
              {formError}
            </div>
          )}

          {formSuccess && (
            <div className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
              {formSuccess}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Lucide icon="Save" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Save changes
                </>
              )}
            </Button>
            <Button type="button" variant="outline-primary" onClick={() => navigate('/manage-users')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Main;
